import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setBulkActionProgress as reduxSetBulkActionProgress } from '../../actions/progress'
import { axiosGet, axiosPatch } from '../../util/api'
import { reloadPage } from '../../util/reload'
import { Toasts } from '../toaster/Toast'
import {
    BulkActionDialog,
    BulkActionDialogActions,
    BulkActionField,
    bulkActionInputClassName,
} from '../ui/BulkActionDialog'
import {
    type BulkActionTerminalOutcome,
    extractProgressUuid,
    registerBulkActionRun,
    terminalResultHasMutation,
} from '../ui/bulkActionExecution'
import {
    CAMPAIGN_BID_ADJUSTMENTS,
    type CampaignBidAdjustmentValues,
} from './CampaignBidAdjustments'

/** Percentage points, never divided (see `util/placement_adjustments.ts`). */
const MAX_ADJUSTMENT = 900

/** Row field -> the bulk-action update key the engine reads. */
export const BID_ADJUSTMENT_UPDATE_KEYS = {
    bid_adjustment_top_of_search: 'tos_placement',
    bid_adjustment_rest_of_search: 'ros_placement',
    bid_adjustment_product_page: 'pp_placement',
} as const satisfies Record<keyof CampaignBidAdjustmentValues, string>

export interface CampaignBidAdjustmentsModalProps {
    isOpen: boolean
    onClose: () => void
    campaign: CampaignBidAdjustmentValues & {
        campaign_id: unknown
        campaign_name?: string
    }
}

const currentValue = (
    campaign: CampaignBidAdjustmentValues,
    key: keyof CampaignBidAdjustmentValues,
) => campaign[key] ?? 0

const isValid = (raw: string) => {
    if (raw.trim() === '') {
        return false
    }
    const numeric = Number(raw)
    return Number.isInteger(numeric) && numeric >= 0 && numeric <= MAX_ADJUSTMENT
}

const initialDrafts = (campaign: CampaignBidAdjustmentValues) =>
    Object.fromEntries(
        CAMPAIGN_BID_ADJUSTMENTS.map(({ key }) => [key, String(currentValue(campaign, key))]),
    ) as Record<keyof CampaignBidAdjustmentValues, string>

/**
 * Edits one campaign's three placement bid adjustments in a single bulk-action run
 * (MER-2987).
 *
 * Only changed fields are submitted. The engine plans one item per field and sends
 * one Amazon optimization call per campaign, so an unchanged placement causes no
 * mutation at all.
 *
 * Confirmed values arrive through the table refresh, not an optimistic write: Amazon
 * is the authority on what actually applied.
 */
export const CampaignBidAdjustmentsModal = ({
    isOpen,
    onClose,
    campaign,
}: CampaignBidAdjustmentsModalProps) => {
    const dispatch = useDispatch()
    const [drafts, setDrafts] = useState(() => initialDrafts(campaign))
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (isOpen) {
            setDrafts(initialDrafts(campaign))
            setLoading(false)
        }
    }, [isOpen, campaign])

    const changed = useMemo(
        () =>
            CAMPAIGN_BID_ADJUSTMENTS.filter(
                ({ key }) =>
                    isValid(drafts[key]) && Number(drafts[key]) !== currentValue(campaign, key),
            ),
        [drafts, campaign],
    )

    const allValid = CAMPAIGN_BID_ADJUSTMENTS.every(({ key }) => isValid(drafts[key]))
    const canSubmit = allValid && changed.length > 0 && !loading

    const onComplete = (uuid: string, outcome?: BulkActionTerminalOutcome) => {
        const present = (result: any) => {
            if (result && terminalResultHasMutation(result)) {
                Toasts.success('Bid adjustments updated')
            } else {
                Toasts.success('Bid adjustments already up to date')
            }
            // A full reload is the only refresh that cannot show a stale row: the
            // dialog stays up, spinner and all, until the page goes.
            reloadPage()
        }
        const fail = (error: any) => {
            Toasts.error(
                error?.response?.data?.message ?? 'The bid adjustment update did not complete.',
            )
            // A campaign's placements apply or fail together, so a failed run changed
            // nothing. Keep the dialog open with the drafts intact so the user can
            // retry without retyping.
            setLoading(false)
        }

        if (outcome?.response !== undefined) {
            present(outcome.response)
            return
        }
        if (outcome?.error !== undefined) {
            fail(outcome.error)
            return
        }

        axiosGet('bulk-actions/run-info', { uuid }, false, { ignore: true })
            .then(present)
            .catch(fail)
    }

    const submit = () => {
        if (!canSubmit) {
            return
        }
        setLoading(true)

        const update = Object.fromEntries(
            changed.map(({ key }) => [BID_ADJUSTMENT_UPDATE_KEYS[key], Number(drafts[key])]),
        )

        axiosPatch('bulk-actions/update', {
            class: 'Campaigns',
            selection: {
                all: false,
                // The raw row value: a campaign id can exceed Number.MAX_SAFE_INTEGER.
                safeList: [campaign.campaign_id],
                blockList: [],
                selectedCount: 1,
            },
            update,
        })
            .then((res) => {
                const progress = res.data?.progress
                const awaitingAcceptance = res.data?.progress_pending === true
                const uuid = awaitingAcceptance
                    ? res.data?.request_uuid
                    : extractProgressUuid(progress, 'Campaigns')

                if (uuid) {
                    // Acceptance is not completion. The dialog stays open and locked
                    // until the registered handler fires, so the user can see the run
                    // is still going rather than watching an unchanged table.
                    registerBulkActionRun(uuid, onComplete, awaitingAcceptance)
                    dispatch(reduxSetBulkActionProgress(progress ?? {}))
                    return
                }

                // Without a uuid nothing can report completion, so holding the dialog
                // open would hang it.
                Toasts.error('The bulk action started without a progress ID. Please refresh.')
                setLoading(false)
                onClose()
            })
            .catch(() => {
                // axiosPatch's shared handler has already toasted the failure.
                setLoading(false)
            })
    }

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={onClose}
            dismissible={!loading}
            title="Edit Bid Adjustments"
            description={campaign.campaign_name}
            width="compact"
            footer={
                <BulkActionDialogActions
                    primaryLabel="Save"
                    loadingLabel="Saving…"
                    loading={loading}
                    disabled={!canSubmit}
                    onCancel={onClose}
                    onPrimary={submit}
                />
            }
        >
            {CAMPAIGN_BID_ADJUSTMENTS.map(({ key, label }) => {
                const inputId = `bid-adjustment-${key}`
                return (
                    <BulkActionField
                        key={key}
                        label={label}
                        htmlFor={inputId}
                        error={
                            isValid(drafts[key])
                                ? undefined
                                : `Enter a whole number from 0 to ${MAX_ADJUSTMENT}.`
                        }
                    >
                        <div className="relative">
                            <span className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-[42px] items-center justify-center border-r border-[#d8e0e8] text-[14px] font-medium text-[#5f6b7c]">
                                %
                            </span>
                            <input
                                id={inputId}
                                disabled={loading}
                                min={0}
                                max={MAX_ADJUSTMENT}
                                step={1}
                                type="number"
                                value={drafts[key]}
                                onChange={(e) =>
                                    setDrafts((previous) => ({
                                        ...previous,
                                        [key]: e.target.value,
                                    }))
                                }
                                className={`${bulkActionInputClassName} pl-[54px]`}
                            />
                        </div>
                    </BulkActionField>
                )
            })}
            <p className="m-0 text-[13px] leading-[18px] text-[#738091]">0 clears an adjustment.</p>
        </BulkActionDialog>
    )
}
